/**
 * Campaign Schedule Card (Redaktionsplan)
 *
 * Timetable view inside the chat: per LinkedIn account the scheduled,
 * running, and recently finished campaigns on a simple timeline, when the
 * account becomes free, and scheduling conflicts.
 */

import React from 'react';
import { Box, Paper, Typography, Chip, Stack, Alert } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

interface ScheduleEntry {
  campaignId: string;
  name: string;
  state: 'scheduled' | 'running' | 'finished';
  scheduledStartAt?: string | null;
  estimatedEndDate?: string | null;
  openContacts?: number;
  finishedAt?: string;
}

export interface CampaignScheduleCardData {
  timeline: Array<{
    accountId: string;
    accountName: string;
    freeFrom: string | null;
    entries: ScheduleEntry[];
    conflicts: string[];
  }>;
}

function fmt(date?: string | null): string {
  if (!date) return '';
  try {
    return new Date(date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return String(date);
  }
}

const STATE_META: Record<ScheduleEntry['state'], { color: 'default' | 'success' | 'primary'; icon: React.ReactNode }> = {
  scheduled: { color: 'primary', icon: <EventIcon sx={{ fontSize: '0.9rem' }} /> },
  running: { color: 'success', icon: <PlayArrowIcon sx={{ fontSize: '0.9rem' }} /> },
  finished: { color: 'default', icon: <CheckCircleOutlineIcon sx={{ fontSize: '0.9rem' }} /> },
};

const CampaignScheduleCard: React.FC<{ data: CampaignScheduleCardData }> = ({ data }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const timeline = data.timeline || [];

  return (
    <Paper className={classes.card} elevation={0}>
      <Stack direction="row" alignItems="center" gap={1}>
        <CalendarMonthIcon color="primary" fontSize="small" />
        <Typography variant="subtitle2" className={classes.title}>
          {t('scheduleTitle', 'Redaktionsplan')}
        </Typography>
      </Stack>

      {timeline.map((account) => (
        <Box key={account.accountId} className={classes.accountBlock}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" gap={1}>
            <Typography variant="body2" className={classes.accountName}>
              {account.accountName}
            </Typography>
            {account.freeFrom && (
              <Typography variant="caption" color="textSecondary">
                {account.freeFrom === 'sofort'
                  ? t('scheduleFreeNow', 'frei — sofort planbar')
                  : t('scheduleFreeFrom', 'voraussichtlich frei ab {{date}}', { date: fmt(account.freeFrom) })}
              </Typography>
            )}
          </Stack>

          {account.entries.map((entry) => {
            const meta = STATE_META[entry.state];
            return (
              <Stack key={entry.campaignId} direction="row" alignItems="center" gap={1} className={classes.entryRow}>
                <Chip
                  size="small"
                  color={meta.color}
                  icon={meta.icon as React.ReactElement}
                  label={
                    entry.state === 'scheduled'
                      ? t('scheduleStateScheduled', 'ab {{date}}', { date: fmt(entry.scheduledStartAt) })
                      : entry.state === 'running'
                        ? (entry.estimatedEndDate
                          ? t('scheduleStateRunning', 'läuft bis ~{{date}}', { date: fmt(entry.estimatedEndDate) })
                          : t('scheduleStateRunningOpen', 'läuft'))
                        : t('scheduleStateFinished', 'beendet {{date}}', { date: fmt(entry.finishedAt) })
                  }
                  className={classes.stateChip}
                />
                <Typography variant="body2" noWrap className={classes.entryName}>
                  {entry.name}
                </Typography>
                {typeof entry.openContacts === 'number' && (
                  <Typography variant="caption" color="textSecondary" className={classes.openCount}>
                    {t('scheduleOpenContacts', '{{count}} offen', { count: entry.openContacts })}
                  </Typography>
                )}
              </Stack>
            );
          })}

          {account.conflicts.map((conflict, i) => (
            <Alert key={i} severity="warning" className={classes.conflict}>
              {conflict}
            </Alert>
          ))}
        </Box>
      ))}

      {timeline.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          {t('scheduleEmpty', 'Noch nichts geplant und nichts aktiv.')}
        </Typography>
      )}
    </Paper>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    padding: theme.spacing(1.5),
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    maxWidth: 560,
  },
  title: {
    fontWeight: 600,
  },
  accountBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    paddingTop: theme.spacing(0.5),
    borderTop: `1px dashed ${theme.palette.divider}`,
  },
  accountName: {
    fontWeight: 600,
  },
  entryRow: {
    minWidth: 0,
  },
  stateChip: {
    flexShrink: 0,
    minWidth: 130,
    justifyContent: 'flex-start',
  },
  entryName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexGrow: 1,
  },
  openCount: {
    flexShrink: 0,
  },
  conflict: {
    padding: theme.spacing(0.25, 1),
  },
}));

export default CampaignScheduleCard;
